
import moment from 'moment';
import React from 'react';
import view_day_ar from '../../functions/view_day_ar';
import FooterInvoice from '../../bookings/invoice/FooterInvoice';
import HeaderInvoice from '../../bookings/invoice/HeaderInvoice';
import '../../bookings/payments/views/invoices/invoice.css';

/**
 * InvoiceReportSummary
 * A printable invoice for the grouped daily check-in report summary.
 * Props:
 *   summaryData   - array of grouped rows (name, total_days, total_people, total_price, paymentTypes{})
 *   totals        - { days, people, price, paid, paymentTypeTotals{} }
 *   date          - Date object selected by the user
 *   paymentTypes  - array of payment type objects from the API { id_payment_type, name_payment_type }
 */
function InvoiceReportSummary({ summaryData = [], totals = {}, date = null, paymentTypes = [] }) {

    const filteredPaymentTypes = paymentTypes?.filter(pt => pt.id_payment_type !== undefined) ?? [];
    const grandTotalPaid = Object.values(totals.paymentTypeTotals ?? {}).reduce((a, b) => a + b, 0);
    const grandTotalDue = (totals.price ?? 0) - grandTotalPaid;

    return (
        <div className='col-md-12'>
            <div id="invoice" className="page-content fade-in-up" dir="rtl" style={{ padding: "10px" }}>
                <div>
                    {/* ─── Header ─────────────────────────────────── */}
                    <div className="invoice-header">
                        <div className="row">
                            <div className="col-12">
                                <HeaderInvoice title={
                                    <>
                                        ملخص حركة يوم
                                        <hr />
                                        {view_day_ar(date)}
                                        {" " + moment(date).format("DD-MM-Y")}
                                    </>
                                } />
                            </div>
                        </div>
                    </div>

                    {/* ─── Grouped Summary Table ───────────────────── */}
                    <div className="table-responsive">
                        <table className="table table-bordered table-striped no-margin table-invoice">
                            <thead className="color-view bg-">
                                <tr>
                                    <th style={{ fontSize: "13px" }}>مكان الإقامة / الخدمة</th>
                                    <th style={{ fontSize: "13px" }}>الأيام</th>
                                    <th style={{ fontSize: "13px" }}>الأشخاص</th>
                                    <th style={{ fontSize: "13px" }}>السعر الكلي</th>
                                    {filteredPaymentTypes.map(item => (
                                        <th key={item.id_payment_type} style={{ fontSize: "13px" }}>
                                            {item.name_payment_type}
                                        </th>
                                    ))}
                                    <th style={{ fontSize: "13px" }}>إجمالي المدفوع</th>
                                    <th style={{ fontSize: "13px" }}>إجمالي المستحق</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: "12px" }}>
                                {summaryData?.map((row) => {
                                    const rowPaid = Object.values(row.paymentTypes ?? {}).reduce((a, b) => a + b, 0);
                                    const rowDue = (row.total_price ?? 0) - rowPaid;
                                    return (
                                        <tr key={row.key}>
                                            <td><strong>{row.name}</strong></td>
                                            <td>{row.total_days}</td>
                                            <td>{row.total_people}</td>
                                            <td>
                                                <strong>
                                                    {Intl.NumberFormat("en-US").format(row.total_price)} د.ل
                                                </strong>
                                            </td>
                                            {filteredPaymentTypes.map(pt => {
                                                const amt = row.paymentTypes?.[pt.id_payment_type] ?? 0;
                                                return (
                                                    <td key={pt.id_payment_type}
                                                        style={{ color: amt < 0 ? "red" : amt > 0 ? "green" : "inherit" }}>
                                                        {Intl.NumberFormat("en-US").format(amt)} د.ل
                                                    </td>
                                                );
                                            })}
                                            <td>
                                                <strong style={{ color: "green" }}>
                                                    {Intl.NumberFormat("en-US").format(rowPaid)} د.ل
                                                </strong>
                                            </td>
                                            <td>
                                                <strong style={{ color: rowDue > 0 ? "red" : "inherit" }}>
                                                    {Intl.NumberFormat("en-US").format(rowDue)} د.ل
                                                </strong>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* Totals row */}
                                <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                                    <td><strong>الإجمالي </strong></td>
                                    <td>{totals.days}</td>
                                    <td>{totals.people}</td>
                                    <td>{Intl.NumberFormat("en-US").format(totals.price)} د.ل</td>
                                    {filteredPaymentTypes.map(item => {
                                        const amt = totals.paymentTypeTotals?.[item.id_payment_type] ?? 0;
                                        return (
                                            <td key={item.id_payment_type}
                                                style={{ color: amt < 0 ? "red" : amt > 0 ? "green" : "inherit" }}>
                                                {Intl.NumberFormat("en-US").format(amt)} د.ل
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <strong style={{ color: "green" }}>
                                            {Intl.NumberFormat("en-US").format(grandTotalPaid)} د.ل
                                        </strong>
                                    </td>
                                    <td>
                                        <strong style={{ color: grandTotalDue > 0 ? "red" : "inherit" }}>
                                            {Intl.NumberFormat("en-US").format(grandTotalDue)} د.ل
                                        </strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ─── Print date ──────────────────────────────── */}
                <br />
                <table>
                    <thead>
                        <tr>
                            <th colSpan={5}>
                                <small style={{ margin: "5px" }}>تاريخ الطباعة</small>
                                {" - "}
                                <small style={{ margin: "5px" }} dir="rtl">
                                    {moment(new Date()).format("YYYY-MM-DD")}
                                </small>
                                {" - "}
                                <small style={{ margin: "5px" }} dir="ltr">
                                    {moment(new Date()).format("LT")}
                                </small>
                            </th>
                        </tr>
                    </thead>
                </table>

                {/* ─── Footer ─────────────────────────────────── */}
                <FooterInvoice />
            </div>
        </div>
    );
}

export default InvoiceReportSummary;
